import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SimpleLeadCapture — Phase 1 marketplace seed lead form
 *
 * Captures: email + company name + role (business or vendor)
 * No password, no signup — just lead capture for marketplace seeding
 */
const SimpleLeadCapture = ({ defaultRole = null, source = 'landing-hero' }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    company_name: '',
    role: defaultRole || 'business', // business or vendor
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.company_name.trim()) return;

    setStatus('loading');
    try {
      await axios.post(`${API}/leads/capture`, {
        email: formData.email.trim(),
        company_name: formData.company_name.trim(),
        role: formData.role,
        source,
      });
      setStatus('success');

      // Track conversion event
      if (window.gtag) {
        window.gtag('event', 'lead_captured', {
          role: formData.role,
          source,
        });
      }
    } catch (error) {
      console.error('Lead capture failed:', error);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-green-900 mb-2">
          {t('landing.leadCapture.successTitle') || "You're in!"}
        </h3>
        <p className="text-sm text-green-700">
          {t('landing.leadCapture.successMessage') || "We'll reach out soon to get you started."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
      <div>
        <Label htmlFor="lead-email" className="text-sm font-medium text-slate-700">
          {t('landing.leadCapture.emailLabel') || 'Work Email'}
        </Label>
        <Input
          id="lead-email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="you@company.com"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="lead-company" className="text-sm font-medium text-slate-700">
          {t('landing.leadCapture.companyLabel') || 'Company Name'}
        </Label>
        <Input
          id="lead-company"
          type="text"
          required
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          placeholder={t('landing.leadCapture.companyPlaceholder') || 'Acme Corp'}
          className="mt-1"
        />
      </div>

      {!defaultRole && (
        <div>
          <Label htmlFor="lead-role" className="text-sm font-medium text-slate-700">
            {t('landing.leadCapture.roleLabel') || 'I am a...'}
          </Label>
          <select
            id="lead-role"
            required
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="business">
              {t('landing.leadCapture.roleBusiness') || 'Business (looking for vendors)'}
            </option>
            <option value="vendor">
              {t('landing.leadCapture.roleVendor') || 'Vendor (offering services)'}
            </option>
          </select>
        </div>
      )}

      <Button
        type="submit"
        disabled={status === 'loading'}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('landing.leadCapture.submitting') || 'Submitting...'}
          </>
        ) : (
          t('landing.leadCapture.submit') || 'Join the Marketplace'
        )}
      </Button>

      {status === 'error' && (
        <p className="text-xs text-red-600 mt-2">
          {t('landing.leadCapture.error') || 'Something went wrong. Please try again.'}
        </p>
      )}

      <p className="text-xs text-slate-500 text-center mt-3">
        {t('landing.leadCapture.privacy') || 'No credit card required. We respect your privacy.'}
      </p>
    </form>
  );
};

export default SimpleLeadCapture;
